import axios from "axios";
import normalize from "Utils/normalizer";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import { toLower, omitBy, isEmpty, isNil, map, some } from "lodash";
import { DriverApprovalStatus, DriverCreationFormType } from "Types/new_driver_types";
import { DefaultFilter, Pagination, Sorting, Statuses } from "Types/common.types";
import dataTableFilter from "Components/shared/data-table-filter";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Filters extends DefaultFilter {
  logisticProviderOrgID?: string;
  companyOrgID?: string;
  riderType?: string;
  orFields?: string;
}

// TODO: createDriver and createExternalDriver can be combine into one function
/**
 * const endpoint = {
 *   internal : "/org/driver/create",
 *   external : "/auth/driver/create",
 * }
 *
 * in `response.url`:  ${url}${endpoint[(data.riderType).toLowerCase()]}
 */

export const createDriver = async (data: DriverCreationFormType) => {
  const accessToken = getAccessToken();
  // data={
  //     ...data,
  //     riderType:"INTERNAL",
  // }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/create`,
      data: normalize.model.new_driver_create(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createDriver(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createExternalDriver = async (data: DriverCreationFormType) => {
  const accessToken = getAccessToken();
  // data={
  //     ...data,
  //     riderType:"EXTERNAL",
  // }
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/auth/driver/create`,
      data: normalize.model.new_driver_create(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createExternalDriver(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDriverList = async (
  filters: Filters,
  orgIDs: string[],
  approvalStatus?: DriverApprovalStatus,
  tempDelete?: boolean,
  onlineStatus?: boolean,
  pagination?: Pagination,
  sorting?: Sorting
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/list`,
      data: omitBy(
        {
          filters: omitBy(
            {
              ...filters,
              orgID: filters?.companiesFilter ? [filters?.companiesFilter] : orgIDs,
              companiesFilter: null,
            },
            isEmpty
          ),
          approvalStatus,
          tempDelete,
          onlineStatus,
          pagination,
          sorting,
        },
        isNil
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.drivers;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchDriverList(
          filters,
          orgIDs,
          approvalStatus,
          tempDelete,
          onlineStatus,
          pagination,
          sorting
        );
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDriver = async (id: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/fetch`,
      data: {
        profileID: id,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.driver_management(response.data);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchDriver(id);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchRidersStats = async (id: string, companyIds?: string[], lpIds?: string[]) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/statistics/list`,
      data: omitBy(
        {
          filters: omitBy(
            {
              logisticProviderOrgID: lpIds,
              companyOrgID: companyIds,
            },
            isEmpty
          ),
          orgID: id,
        },
        isEmpty
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.statistics;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchRidersStats(id, companyIds, lpIds);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateDriver = async (data) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/update`,
      data: normalize.model.driver_update(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateDriver(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDiolkoCompaniesList = async (orgID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/company/list`,
      data: omitBy(
        {
          orgID: orgID,
        },
        isNil
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchDiolkoCompaniesList(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDriverStatisticCount = async (orgID: string[]) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/statistics/list`,
      data: { orgID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchDriverStatisticCount(orgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const driverActivation = async (
  profileID: string,
  tempDelete: boolean,
  tempDeletedReason?: string
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/activation/update`,
      data: { profileID, tempDelete, tempDeletedReason },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await driverActivation(profileID, tempDelete, tempDeletedReason);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const driverApproval = async (payload) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/driver/approval/update`,
      data: payload,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await driverApproval(payload);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchLogisticProviderList = async (id) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/org/logistic_provider/list`,
      data: {
        orgID: id,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.orgs;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchLogisticProviderList(id);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
